import { useAuthStore } from "@/stores";
import { getMockData } from "@/services/mockApi";
import { createHttpError } from "@/services/HttpError";
import { currentDefaultVersion } from "@/assets/mobiusVersioningData";

const SERVICE_URLS = {
  cwacc: `/api/cwacc/${currentDefaultVersion}`,
  psa: "/api/psa",
  branding: "/api/branding",
  "tenant-branding": "/api/tenant-branding",
  payments: "/api/payments",
  statuses: "/api/statuses",
  "tenant-management": "/api/tenant-management"
};

// NOTE: These methods are essentially similar to what axios does.

function constructUrl(service: keyof typeof SERVICE_URLS, path: string): string {
  let cleanPath = path;
  if (!cleanPath.startsWith("/")) cleanPath = `/${cleanPath}`;

  return `${SERVICE_URLS[service]}${cleanPath}`;
}

function buildQueryString(params: Record<string, unknown> = {}): string {
  const q = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    const val = params[key];
    if (val !== undefined) {
      if (Array.isArray(val)) {
        // Handle arrays by appending multiple entries with the same key
        val.forEach(item => {
          q.append(key, String(item));
        });
      } else {
        q.set(key, String(val));
      }
    }
  });

  const result = q.toString();
  return result ? `?${result}` : "";
}

// Service helpers are generic over the response type. Each call site declares
// the shape it expects from the API; runtime safety is the caller's
// responsibility (the helpers do not validate). The previous pattern of
// returning `unknown` and casting at every call site obscured the contract
// without adding any safety, so we lean on generics + explicit interfaces.

export async function fetchPage<T = unknown>(
  serviceName: keyof typeof SERVICE_URLS,
  path: string,
  params: Record<string, unknown> = {}
): Promise<T> {
  const mockData = await getMockData(serviceName, path);
  if (mockData !== null) return mockData as T;

  const authStore = useAuthStore();
  const { authFetch } = authStore;

  const fullUrl = constructUrl(serviceName, path) + buildQueryString(params);
  const response = await authFetch(fullUrl, { method: "GET" });
  if (!response.ok) {
    throw await createHttpError(response, fullUrl);
  }
  if (response.status === 204) return response as T;
  return response.json() as Promise<T>;
}

export async function postPage<T = unknown, B extends object | FormData = object>(
  serviceName: keyof typeof SERVICE_URLS,
  path: string,
  data?: B,
  params: Record<string, unknown> = {},
  method = "POST"
): Promise<T> {
  const authStore = useAuthStore();
  const { authFetch } = authStore;

  const fullUrl = constructUrl(serviceName, path) + buildQueryString(params);
  const isFormData = data instanceof FormData;
  const headers: Record<string, string> = {};

  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  const response = await authFetch(fullUrl, {
    method,
    headers,
    body: isFormData ? data : JSON.stringify(data ?? {})
  });
  if (!response.ok) {
    throw await createHttpError(response, fullUrl);
  }
  if (response.status === 204) return response as T;

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("text/plain")) {
    return response.text() as Promise<T>;
  }

  return response.json() as Promise<T>;
}

export async function deletePage<T = unknown>(
  serviceName: keyof typeof SERVICE_URLS,
  path: string,
  params: Record<string, unknown> = {}
): Promise<T> {
  const authStore = useAuthStore();
  const { authFetch } = authStore;

  const fullUrl = constructUrl(serviceName, path) + buildQueryString(params);
  const response = await authFetch(fullUrl, { method: "DELETE" });
  if (!response.ok) {
    throw await createHttpError(response, fullUrl);
  }
  if (response.status === 204) return response as T;

  return response.json() as Promise<T>;
}
